import "./Performance.css";
import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "jquery-ui/ui/widgets/datepicker";
import "select2/dist/css/select2.min.css";
import "select2";
import { DatePicker, InputBox } from "../../components/Input";
import { API_URL } from "../../config";
import axios from "axios";
import StateManagedSelect from "react-select";
import { showToast } from "../../components/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../../helpers/permission.js";
import usePersistedState from "../../Subscriber/usePersistedState.jsx";

// Replace status dropdown with StateManagerSelect
const statusOptions = [
  { value: "", label: "--select--" },
  { value: "2", label: "Target 1" },
  { value: "3", label: "Target 2" },
  { value: "4", label: "StopLoss" },
  { value: "5", label: "Closed" },
];

const segmentOption = [
  { value: "", label: "--select--" },
  { value: "1", label: "Trading Street" },
  { value: "2", label: "Investment Street" },
  { value: "3", label: "Commodity Street" },
];

const Performance = () => {
  const [loading, setLoading] = useState(false);
  const [generateResultLoading, setGenerateResultLoading] = useState(false);
  const [generateResearchLoading, setGenerateResearchLoading] = useState(false);
  const [exportResearchLoading, setExportResearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = localStorage.getItem("authToken");
  const [serviceOptions, setserviceOptions] = useState([]);
  const [sortBy, setSortBy] = useState("id");
  const [sortDirection, setSortDirection] = useState("DESC");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;
  const [performanceData, setPerformanceData] = useState([]);

  // Persisted filters (mirror Order page behavior)
  const [segid, setSegid] = usePersistedState("performance_segid", "");
  const [serviceid, setServiceid] = usePersistedState("performance_serviceid", "");
  const [symbol_name, setSymbolName] = usePersistedState("performance_symbol_name", "");
  const [unique_id, setUniqueId] = usePersistedState("performance_unique_id", "");
  const [status, setStatus] = usePersistedState("performance_status", "");
  const [sdate, setSdate] = usePersistedState("performance_sdate", "");
  const [edate, setEdate] = usePersistedState("performance_edate", "");

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}service/active-services`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const select = {
        value: "",
        label: "--select--",
      };

      const formattedData = [
        select,
        ...response.data.data.map((dt) => ({
          value: dt.id,
          label: dt.service_name,
        })),
      ];

      setserviceOptions(formattedData);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPerformanceData = async (overrides = {}) => {
    try {
      setLoading(true);
      const qSegid = overrides.segid !== undefined ? overrides.segid : segid;
      const qServiceid = overrides.serviceid !== undefined ? overrides.serviceid : serviceid;
      const qStatus = overrides.status !== undefined ? overrides.status : status;
      const qSymbol = overrides.symbol_name !== undefined ? overrides.symbol_name : symbol_name;
      const qUniqueId = overrides.unique_id !== undefined ? overrides.unique_id : unique_id;
      const qSdate = overrides.sdate !== undefined ? overrides.sdate : sdate;
      const qEdate = overrides.edate !== undefined ? overrides.edate : edate;
      const qPage = overrides.page !== undefined ? overrides.page : page;
      const response = await axios.get(
        `${API_URL}researchCalls?segid=${qSegid}&serviceid=${qServiceid}&status=${qStatus}&symbol_name=${qSymbol}&unique_id=${qUniqueId}&sdate=${qSdate}&edate=${qEdate}&sortby=${sortDirection}&sortbycol=${sortBy}&limit=${limit}&page=${qPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setPerformanceData(response.data.data.data);
      setPageCount(response.data.data.totalPages);
      setTotal(response.data.data.total);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformanceData();
    fetchServices();
  }, [sortBy, sortDirection, page]);

  useEffect(() => {
    const handleClearFilters = () => {
      handleClear();
    };

    window.addEventListener('clearResearchPerformanceFiltersEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearResearchPerformanceFiltersEvent', handleClearFilters);
    };
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "symbol_name") setSymbolName(value);
    else if (name === "unique_id") setUniqueId(value);
    else if (name === "sdate") setSdate(value);
    else if (name === "edate") setEdate(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    fetchPerformanceData(); // Refresh data after submission
  };

  const hasAnyFilter = () => {
    return (
      segid !== "" ||
      serviceid !== "" ||
      symbol_name !== "" ||
      unique_id !== "" ||
      status !== "" ||
      sdate !== "" ||
      edate !== ""
    );
  };

  const handleClear = () => {
    // Reset states
    setSegid("");
    setServiceid("");
    setSymbolName("");
    setUniqueId("");
    setStatus("");
    setSdate("");
    setEdate("");

    // Remove persisted keys explicitly
    try {
      localStorage.removeItem("performance_segid");
      localStorage.removeItem("performance_serviceid");
      localStorage.removeItem("performance_symbol_name");
      localStorage.removeItem("performance_unique_id");
      localStorage.removeItem("performance_status");
      localStorage.removeItem("performance_sdate");
      localStorage.removeItem("performance_edate");
    } catch (_) { }

    setPage(1);
    // Fetch immediately with cleared filters
    fetchPerformanceData({
      segid: "",
      serviceid: "",
      status: "",
      symbol_name: "",
      unique_id: "",
      sdate: "",
      edate: "",
      page: 1,
    });
  };


  // removed filtersReset logic; fetching is controlled by submit/clear/sort/page

  const handleSort = (column) => {
    const newDirection =
      sortBy === column && sortDirection === "ASC" ? "DESC" : "ASC";
    setSortBy(column);
    setSortDirection(newDirection);
  };

  const handleGenerateResult = async () => {
    try {
      setGenerateResultLoading(true);
      const response = await axios.get(
        `${API_URL}researchCalls/generateResult?segid=${segid}&serviceid=${serviceid}&status=${status}&symbol_name=${symbol_name}&unique_id=${unique_id}&sdate=${sdate}&edate=${edate}&sortby=${sortDirection}&sortbycol=${sortBy}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        showToast.success("Performance Generated Successfully");
      }
    } catch (error) {
      setError(error.message);
      showToast.error("Error generating performance");
    } finally {
      setGenerateResultLoading(false);
    }
  };

  const handleGenerateResearchCall = async () => {
    try {
      setGenerateResearchLoading(true);
      const response = await axios.get(
        `${API_URL}researchCalls/generateResearchCall?segid=${segid}&serviceid=${serviceid}&status=${status}&symbol_name=${symbol_name}&unique_id=${unique_id}&sdate=${sdate}&edate=${edate}&sortby=${sortDirection}&sortbycol=${sortBy}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        showToast.success("Research Call Generated Successfully");
      }
    } catch (error) {
      showToast.error("Error generating research call");
      setError(error.message);
    } finally {
      setGenerateResearchLoading(false);
    }
  };

  const handleExportResearchCall = async () => {
    try {
      setExportResearchLoading(true);
      const response = await axios.get(
        `${API_URL}researchCalls/exportPerformance?segid=${segid}&serviceid=${serviceid}&status=${status}&symbol_name=${symbol_name}&unique_id=${unique_id}&sdate=${sdate}&edate=${edate}&sortby=${sortDirection}&sortbycol=${sortBy}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );
      if (response.status === 200) {
        // Generate timestamp-based filename
        const now = new Date();
        const pad = (n) => String(n).padStart(2, "0");

        const day = pad(now.getDate());
        const month = pad(now.getMonth() + 1); // Months are 0-based
        const year = now.getFullYear();

        let hour = now.getHours();
        const minutes = pad(now.getMinutes());
        const seconds = pad(now.getSeconds());
        const ampm = hour >= 12 ? "PM" : "AM";
        hour = hour % 12 || 12; // Convert to 12-hour format

        const timeStr = `${hour}_${minutes}_${seconds}_${ampm}`;
        const filename = `PerformanceReport${day}_${month}_${year}_${timeStr}.xlsx`;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        // Give it a filename (you can also get this from headers if backend sends it)
        link.setAttribute("download", filename);

        // Append to DOM and trigger download
        document.body.appendChild(link);
        link.click();

        // Clean up
        link.remove();
        window.URL.revokeObjectURL(url);
        showToast.success("Performance report exported successfully.");
      }
    } catch (error) {
      showToast.error(error.message);
    } finally {
      setExportResearchLoading(false);
    }
  };

  const handleTelegramShare = async (id) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}researchCalls/${id}/notification-share`,
        {}, // Empty data object since you're not sending any data in the body
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success || response.status === 201) {
        // Check response.data.success instead of response.status
        showToast.success("Shared to Telegram successfully");
      } else {
        showToast.error(response.data.message || "Failed to share on Telegram");
      }
    } catch (error) {
      showToast.error(
        error.response?.data?.message || "Failed to share on Telegram"
      );
      console.error("Telegram share error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async (id) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}socialsharing/share-notification/${id}`,
        {}, // Empty data object since you're not sending any data in the body
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success || response.status === 201) {
        // Check response.data.success instead of response.status
        showToast.success("Notification shared successfully");
      } else {
        showToast.error(response.data.message || "Failed to share on Telegram");
      }
    } catch (error) {
      showToast.error(
        error.response?.data?.message || "Failed to share Notification"
      );
      console.error("Telegram share error:", error);
    } finally {
      setLoading(false);
    }
  };



  const handleDownloadResearchCall = (research_file) => {
    if (research_file) {
      window.open(research_file, "_blank");
    } else {
      showToast.error("Research file not available");
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonClass: "btn-danger",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const response = await axios.delete(
          `${API_URL}researchCalls/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        setPerformanceData((prev) => prev.filter((dt) => dt.id !== id));
        Swal.fire(
          "Deleted!",
          response.data.message || "Your record has been deleted.",
          "success"
        );
        fetchPerformanceData();
      } catch (error) {
        Swal.fire(
          "Error",
          error.response?.data?.message ||
          "Something went wrong while deleting.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <div
        id="page-container"
        className="page-header-fixed page-sidebar-fixed fade in"
      >
        <div id="content" className="">
          {loading}
          <div className="pb-2 justify-content-between align-items-center">
            <div className="">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/">
                    <i className="ti ti-home"></i> Home
                  </Link>
                </li>
                <li className="breadcrumb-item active">Manage Performance</li>
              </ul>
            </div>
            <div className="d-flex flex-row-reverse gap-2">
              <div>
                <span style={{ fontSize: "0.8rem" }} className="me-2">
                  {start} to {end} of {total}
                </span>
                <button
                  className="btn btn-default btn-sm"
                  title="Previous"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                &nbsp;
                <button
                  className="btn btn-default btn-sm"
                  title="Next"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pageCount}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
              <PermissionWrapper module="performance" action="export">
                <button
                  type="button"
                  className="btn btn-default btn-sm pull-right"
                  onClick={handleExportResearchCall}
                  disabled={exportResearchLoading}
                >
                  {exportResearchLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-plus"></i> Export Research Calls
                    </>
                  )}
                </button>
              </PermissionWrapper>
              <PermissionWrapper module="performance" action="add">
                <button
                  type="button"
                  className="btn btn-default btn-sm pull-right"
                  onClick={handleGenerateResearchCall}
                  disabled={generateResearchLoading}
                >
                  {generateResearchLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-share"></i> Generate Research Calls
                    </>
                  )}
                </button>
              </PermissionWrapper>
              <PermissionWrapper module="performance" action="add">
                <button
                  type="button"
                  className="btn btn-default btn-sm pull-right"
                  onClick={handleGenerateResult}
                  disabled={generateResultLoading}
                >
                  {generateResultLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Generating...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-share"></i> Generate Results
                    </>
                  )}
                </button>
              </PermissionWrapper>
              <Link
                className="btn btn-default btn-sm pull-right"
                target="_blank"
                to="/research/performance/shareview"
              >
                <i className="ti ti-search"></i> View Research Calls
              </Link>
              <Link
                className="btn btn-default btn-sm pull-right"
                target="_blank"
                to="/research/performance/dailyshareview"
              >
                <i className="ti ti-search"></i> View Daily Report
              </Link>
              <Link
                className="btn btn-default btn-sm pull-right"
                target="_blank"
                to="/research/performance/dailyshareviewresult"
              >
                <i className="ti ti-search"></i> View Daily Result
              </Link>
            </div>

          </div>

          <div className="panel panel-default with-rounded-corner with-shadow">
            <form onSubmit={handleSubmit}>
              <div className="panel-heading border-0">
                <span className="panel-title">Search</span>
              </div>
              <div className="panel-body">
                <div className="row">
                  <div className="col-md-2">
                    <label>Segment</label>
                    <StateManagedSelect
                      options={segmentOption}
                      value={segmentOption.find(
                        (option) => option.value === segid
                      )}
                      onChange={(selectedOption) =>
                        setSegid(selectedOption.value)
                      }
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>
                  <div className="col-md-2">
                    <label>Service</label>
                    <StateManagedSelect
                      options={serviceOptions}
                      value={serviceOptions.find(
                        (option) => option.value === serviceid
                      )}
                      onChange={(selectedOption) =>
                        setServiceid(selectedOption.value)
                      }
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>
                  <div className="col-md-2">
                    <InputBox
                      label="Symbol"
                      fieldname="symbol_name"
                      placeholder="Enter Symbol"
                      value={symbol_name}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-2">
                    <InputBox
                      label="Unique ID"
                      fieldname="unique_id"
                      placeholder="Enter Unique ID"
                      value={unique_id}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-2">
                    <label>Status</label>
                    <StateManagedSelect
                      options={statusOptions}
                      value={statusOptions.find(
                        (option) => option.value === status
                      )}
                      onChange={(selectedOption) =>
                        setStatus(selectedOption.value)
                      }
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      }}
                    />
                  </div>
                  <div className="col-md-2">
                    <DatePicker
                      label="Start Date"
                      fieldname="sdate"
                      value={sdate}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-2">
                    <DatePicker
                      label="End Date"
                      fieldname="edate"
                      value={edate}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <button type="submit" className="btn btn-primary mx-2">
                    Submit
                  </button>
                  {hasAnyFilter() && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleClear}
                      title="Clear all filters"
                    >
                      Clear Filter
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>

          <PermissionWrapper module="performance" action="view">
            {(loading || performanceData.length > 0) && (
              <div className="table-responsive panel panel-default with-rounded-corner with-shadow p-10">
                <style>
                  {`
                  .sticky-performance-table thead tr:first-child th {
                    position: sticky;
                    top: 0;
                    z-index: 20;
                    background: #f8f9fa;
                  }
                  
                  .sticky-performance-table thead tr:nth-child(2) th {
                    position: sticky;
                    top: 30px; /* Adjust based on your first header row height */
                    z-index: 19;
                    background: white;
                  }
                  
                  .sticky-performance-container {
                    max-height: calc(100vh - 165px);
                    overflow: auto;
                  }
                  
                  .sticky-performance-table th {
                    vertical-align: top;
                  }
                `}
                </style>

                <div className="sticky-performance-container">
                  <table className="table table-condensed table-bordered bg-white sticky-performance-table">
                    <thead>
                      <tr>
                        <th
                          onClick={() => handleSort("created_on")}
                          style={{ cursor: "pointer" }}
                        >
                          DATE{" "}
                          {sortBy === "created_on" ? (
                            <i
                              className={`ti ti-arrow-${sortDirection === "ASC" ? "down" : "up"
                                }`}
                            ></i>
                          ) : (
                            <i className="ti ti-exchange-vertical"></i>
                          )}
                        </th>
                        <th
                          onClick={() => handleSort("service_name")}
                          style={{ cursor: "pointer" }}
                        >
                          SERVICE{" "}
                          {sortBy === "service_name" ? (
                            <i
                              className={`ti ti-arrow-${sortDirection === "ASC" ? "down" : "up"
                                }`}
                            ></i>
                          ) : (
                            <i className="ti ti-exchange-vertical"></i>
                          )}
                        </th>
                        <th
                          onClick={() => handleSort("unique_id")}
                          style={{ cursor: "pointer" }}
                        >
                          UNIQUE ID{" "}
                          {sortBy === "unique_id" ? (
                            <i
                              className={`ti ti-arrow-${sortDirection === "ASC" ? "down" : "up"
                                }`}
                            ></i>
                          ) : (
                            <i className="ti ti-exchange-vertical"></i>
                          )}
                        </th>
                        <th
                          onClick={() => handleSort("symbol")}
                          style={{ cursor: "pointer" }}
                        >
                          SYMBOL{" "}
                          {sortBy === "symbol" ? (
                            <i
                              className={`ti ti-arrow-${sortDirection === "ASC" ? "down" : "up"
                                }`}
                            ></i>
                          ) : (
                            <i className="ti ti-exchange-vertical"></i>
                          )}
                        </th>
                        <th>ACTION</th>
                        <th>DAYS</th>
                        <th>Qty</th>
                        <th>EP</th>
                        <th>TGT1</th>
                        <th>TGT2</th>
                        <th>SL</th>
                        <th>EXP1</th>
                        <th>EXP2</th>
                        <th>ROI</th>
                        <th>GAINS</th>

                        <th
                          onClick={() => handleSort("status")}
                          style={{ cursor: "pointer" }}
                        >
                          STATUS{" "}
                          {sortBy === "status" ? (
                            <i
                              className={`ti ti-arrow-${sortDirection === "ASC" ? "down" : "up"
                                }`}
                            ></i>
                          ) : (
                            <i className="ti ti-exchange-vertical"></i>
                          )}
                        </th>

                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        // Skeleton loading rows (initial load + filter apply)
                        Array.from({ length: 10 }).map((_, skeletonIndex) => (
                          <tr key={`skeleton-${skeletonIndex}`} className="skeleton-row">
                            {Array.from({ length: 17 }).map((_, colIndex) => (
                              <td
                                key={colIndex}
                                style={{ textAlign: colIndex === 0 ? "left" : "center" }}
                              >
                                <div
                                  className="skeleton-text"
                                  style={{
                                    width:
                                      colIndex === 0
                                        ? "80%"
                                        : colIndex === 2
                                          ? "70%"
                                          : "40%",
                                    margin:
                                      colIndex === 0 ? "0" : "0 auto",
                                  }}
                                ></div>
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : performanceData.length > 0 ? (
                        performanceData.map((post, index) => {
                          const getStatusLabel = (status) => {
                            switch (status) {
                              case 2:
                                return (
                                  <span className="label light-green btn-sm m-b-3">
                                    Target 1 Hit
                                  </span>
                                );
                              case 3:
                                return (
                                  <span className="label dark-green btn-sm m-b-3">
                                    Target 2 Hit
                                  </span>
                                );
                              case 4:
                                return (
                                  <span className="label red btn-sm m-b-3">
                                    Stoploss Hit
                                  </span>
                                );
                              case 5:
                                return (
                                  <span className="label light-black btn-sm m-b-3">
                                    Closed
                                  </span>
                                );
                              default:
                                return null;
                            }
                          };

                          const getClassByStatus = (status) => {
                            switch (status) {
                              case 2:
                                return "txt-light-green";
                              case 3:
                                return "txt-dark-green";
                              case 4:
                                return "txt-red";
                              case 5:
                                return "txt-light-black";
                              default:
                                return "";
                            }
                          };

                          const formatDate = (dateStr) => {
                            const date = new Date(dateStr);
                            return date.toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            });
                          };

                          const action =
                            post.action === 1 ? "Buy" : post.action === 2 ? "Sell" : "";
                          const mclass = getClassByStatus(post.status);
                          const gains = parseFloat(
                            post.gains?.replace(/\s/g, "") || 0
                          ).toFixed(0);
                          const roi = post.roi?.replace(/\s/g, "");
                          const statusLabel = getStatusLabel(post.status);

                          return (
                            <tr key={post.id}>
                              <td>{formatDate(post.created_on)}</td>
                              <td>{post.service_name}</td>
                              <td>{post.unique_id || "-"}</td>
                              <td>
                                <PermissionWrapper module="performance" action="edit">
                                  <Link
                                    to={`/research/performance/edit/${post.id}`}
                                    className="text-primary"
                                  >
                                    {post.symbol}
                                  </Link>
                                </PermissionWrapper>
                                <button
                                  type="button"
                                  className="btn btn-link p-0 ml-1"
                                  style={{ float: "right" }}
                                  onClick={() => handleShare(post.id)}
                                  disabled={loading}
                                  title="Share"
                                >
                                  <PermissionWrapper module="performance" action="share">
                                    <i className="ti ti-share text-primary"></i>
                                  </PermissionWrapper>
                                </button>
                              </td>
                              <td>{action}</td>
                              <td>{post.duration}</td>
                              <td>{post.quantity}</td>
                              <td>{post.entry_price}</td>
                              <td>{post.target_price}</td>
                              <td>{post.target2_price}</td>
                              <td>{post.stoploss}</td>
                              <td>{post.exit_price}</td>
                              <td>{post.exit_price2}</td>
                              <td className={mclass} style={{ fontSize: "1.2rem" }}>
                                <b>{roi}</b>
                              </td>
                              <td className={mclass} style={{ fontSize: "1.2rem" }}>
                                <b>{gains}</b>
                              </td>
                              <td>{statusLabel}</td>
                              <td>
                                <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                                  <PermissionWrapper module="performance" action="share">
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-xs"
                                      title="Telegram Share"
                                      onClick={() => handleTelegramShare(post.id)}
                                      disabled={loading}
                                    >
                                      <i className="ti ti-export"></i>
                                    </button>
                                  </PermissionWrapper>

                                  <PermissionWrapper module="performance" action="export">
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-xs"
                                      title="Download PDF"
                                      onClick={() => handleDownloadResearchCall(post.research_file)}
                                      disabled={loading}
                                    >
                                      <i className="ti ti-import"></i>
                                    </button>
                                  </PermissionWrapper>

                                  <PermissionWrapper module="performance" action="delete">
                                    <button
                                      className="btn btn-danger btn-xs"
                                      onClick={() => handleDelete(post.id)}
                                      title="Delete"
                                    >
                                      <i className="ti ti-trash"></i>
                                    </button>
                                  </PermissionWrapper>
                                </div>
                              </td>

                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          {/* No records state kept unchanged */}
                        </tr>
                      )}
                    </tbody>

                  </table>
                </div>
              </div>
            )}

            {performanceData.length === 0 && <div>No records found.</div>}
          </PermissionWrapper>
        </div>

        <Link href="#" data-click="scroll-top" className="btn-scroll-top fade">
          <i className="ti ti-arrow-up"></i>
        </Link>
      </div>
    </>
  );
};

export default Performance;